import { createAsyncThunk } from '@reduxjs/toolkit';

import { MEDIA_CONTENT_PLACEMENT, MEDIA_TYPES, STORY_SCOPES } from '../../constants';
import sendRequest from '../../service/sendRequest';
import callApi from '../../service/callApi';
import buildQuery from '../../utils/buildQuery';
import timeout from '../../utils/timeout';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import {
  buildAlternativeSearchRequest,
  buildStoryScopeRequestFromMedia,
} from '../../activ8/story/media/buildAlternativeSearchRequest';
import { getStoryMediaId, getStoryMediaRef } from '../../activ8/story/media/storyMediaItem';

const STORY_INIT = 'story/STORY_INIT';
const GET_SESSION_MEDIA = 'story/GET_SESSION_MEDIA';
const GET_SESSION = 'story/GET_SESSION';
const SEARCH_SESSION = 'story/SEARCH_SESSION';
const GET_PLATFORM_INFO = 'story/GET_PLATFORM_INFO';
const GENERATE_STORY = 'story/GENERATE_STORY';
const SEARCH_GENERATED_CONTENT = 'story/SEARCH_GENERATED_CONTENT';
const CHANGE_PREVIEW = 'story/CHANGE_PREVIEW';
const SEND_STORY = 'story/SEND_STORY';
const SEARCH_MEDIA_POOL = 'story/SEARCH_MEDIA_POOL';
const GET_USED_MEDIA = 'story/GET_USED_MEDIA';
const UPLOAD_STORY_MEDIA = 'story/UPLOAD_STORY_MEDIA';
const RE_GENERATE_STORY_CONTENT = 'story/RE_GENERATE_STORY_CONTENT';
const GET_MATCHED_STORY_CONTENT = 'story/GET_MATCHED_STORY_CONTENT';
const SET_STORY_CONTENT = 'story/SET_STORY_CONTENT';

const MEDIA_POOL_DESCRIPTION_RETRY_MS = 1500;
const MEDIA_POOL_DESCRIPTION_MAX_WAIT_MS = 90000;
const DESCRIPTION_NOT_READY_STATUS = 417;

let mediaPoolSearchPromise = null;

export const storyInit = createAsyncThunk(STORY_INIT, async ({ scope, projectId, media }, { rejectWithValue }) => {
  if (scope === STORY_SCOPES.ACTIV8 && (!media || !getStoryMediaId(media))) {
    return rejectWithValue('Selected media is required to create a session');
  }

  const { source, params } = buildStoryScopeRequestFromMedia(media);

  const response = await sendRequest(
    () =>
      callApi.post(`/api/story/init/session?projectId=${projectId}`, {
        scope,
        source,
        params,
      }),
    {
      onError: (e) => {
        return rejectWithValue(e);
      },
    },
  );

  return { sessionId: response.data };
});

export const getSession = createAsyncThunk(GET_SESSION, async ({ sessionId }, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/story/session?id=${sessionId}`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  return { sessionId: response.data.id };
});

export const searchSession = createAsyncThunk(SEARCH_SESSION, async ({ projectId }, { rejectWithValue, getState }) => {
  const { page, size, sort, username } = getState().story.sessionsTableSettings;

  const query = buildQuery({ page: page + 1, size, sort });

  const response = await sendRequest(
    () =>
      callApi.post(`/api/story/search/sessions?projectId=${projectId}&${query}`, {
        sessionSearchParams: {
          ...(username ? { username } : {}),
        },
      }),
    {
      onError: (e) => {
        return rejectWithValue(e);
      },
    },
  );

  return { content: response.data.content, totalElements: response.data.totalElements };
});

export const getPlatformInfo = createAsyncThunk(
  GET_PLATFORM_INFO,
  async ({ type = MEDIA_TYPES.IMAGE, placement = MEDIA_CONTENT_PLACEMENT.SINGLE }, { rejectWithValue }) => {
    const query = buildQuery({ type, placement });
    const response = await sendRequest(() => callApi.get(`/api/media/platforms?${query}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    return response.data;
  },
);

export const uploadStoryMedia = createAsyncThunk(UPLOAD_STORY_MEDIA, async ({ files }, { rejectWithValue }) => {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));

  try {
    const response = await sendRequest(() =>
      callApi.post('/api/story/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );

    const linkedContents = toLinkedContents(response.data);

    if (!linkedContents.length) {
      return rejectWithValue('Empty upload response');
    }

    return linkedContents;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const toLinkedContents = (data) => (Array.isArray(data) ? data : Object.values(data ?? {})).filter((item) => item?.id);

const withCacheBustedLink = (content, version) => {
  if (!content?.link) {
    return content;
  }

  const separator = content.link.includes('?') ? '&' : '?';

  return { ...content, link: `${content.link}${separator}v=${encodeURIComponent(version)}` };
};

const isDescriptionNotReady = (error) => error?.response?.status === DESCRIPTION_NOT_READY_STATUS;

const fetchMediaPoolAlternatives = (projectId, requestBody) =>
  sendRequest(() => callApi.post(`/api/story/search/alternatives?projectId=${projectId}`, requestBody), {
    onError: () => {},
  }).then((response) => response.data ?? []);

const retryWhileDescriptionPending = async (request, { signal, until }) => {
  try {
    return await request();
  } catch (error) {
    if (signal.aborted || !isDescriptionNotReady(error) || Date.now() >= until) {
      throw error;
    }

    await timeout(MEDIA_POOL_DESCRIPTION_RETRY_MS);
    return retryWhileDescriptionPending(request, { signal, until });
  }
};

export const searchMediaPool = createAsyncThunk(
  SEARCH_MEDIA_POOL,
  async ({ mode, media = null, message = '', query = '', projectId }, { rejectWithValue, signal, dispatch }) => {
    const { source, params } = buildAlternativeSearchRequest({ mode, media, message, query });
    const requestBody = { scope: STORY_SCOPES.ACTIV8, source, params };
    const request = () => fetchMediaPoolAlternatives(projectId, requestBody);

    try {
      return await retryWhileDescriptionPending(request, {
        signal,
        until: Date.now() + MEDIA_POOL_DESCRIPTION_MAX_WAIT_MS,
      });
    } catch (error) {
      if (signal.aborted) {
        return rejectWithValue(error);
      }

      if (!isDescriptionNotReady(error)) {
        dispatch(somethingWrongNotification());
      }

      return rejectWithValue(error);
    }
  },
);

export const startMediaPoolSearch = (dispatch, args) => {
  if (mediaPoolSearchPromise) {
    mediaPoolSearchPromise.abort();
  }

  mediaPoolSearchPromise = dispatch(searchMediaPool(args));
  return mediaPoolSearchPromise;
};

export const abortMediaPoolSearch = () => {
  if (mediaPoolSearchPromise) {
    mediaPoolSearchPromise.abort();
    mediaPoolSearchPromise = null;
  }
};

export const generateStory = createAsyncThunk(
  GENERATE_STORY,
  async ({ media, message, projectId, ...platforms }, { dispatch, rejectWithValue, getState }) => {
    let sessionId = getState().story.sessionId;

    if (!sessionId) {
      if (!projectId || !media.length) {
        return rejectWithValue('Project ID and selected media are required to create a session');
      }

      const initResult = await dispatch(
        storyInit({
          scope: STORY_SCOPES.ACTIV8,
          media: media[0],
          projectId,
        }),
      );

      if (storyInit.rejected.match(initResult)) {
        return rejectWithValue(initResult.payload ?? initResult.error);
      }

      sessionId = initResult.payload.sessionId;
    }

    const requestBody = {
      sessionId,
      message,

      platforms: Object.entries(platforms)
        .map(([platform, value]) => ({
          platform,
          names: value.map((item) => item.value),
        }))
        .filter(({ names }) => names.length > 0),

      mediaRefs: media.map(getStoryMediaRef),
    };

    await sendRequest(() => callApi.post(`/api/story/generate`, requestBody), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });
    dispatch(searchGeneratedContent());
    dispatch(getUsedMedia({ sessionId }));
  },
);

export const searchGeneratedContent = createAsyncThunk(
  SEARCH_GENERATED_CONTENT,
  async (_, { rejectWithValue, getState }) => {
    const sessionId = getState().story.sessionId;
    const { page, sort } = getState().story.generatedContentTableSettings;

    if (!sessionId) {
      return rejectWithValue('Session ID is required');
    }

    const query = buildQuery({
      page: page + 1,
      size: 9999,
      sort,
      sessionId,
    });

    const response = await sendRequest(() => callApi.get(`/api/story/search/generated?${query}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    return { content: response.data.content, totalElements: response.data.totalElements };
  },
);

export const getUsedMedia = createAsyncThunk(GET_USED_MEDIA, async ({ sessionId }, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/story/get/used?sessionId=${sessionId}`), {
    onError: (e) => {
      return rejectWithValue(e);
    },
  });

  return response.data;
});

export const changePreview = createAsyncThunk(CHANGE_PREVIEW, async ({ id, preview }, { rejectWithValue }) => {
  await sendRequest(
    () => callApi.post(`/api/story/set/preview?bucketId=${id}&preview=${encodeURIComponent(preview)}`),
    {
      onError: (e) => {
        return rejectWithValue(e);
      },
    },
  );

  return { id, preview };
});

export const reGenerateStoryContent = createAsyncThunk(
  RE_GENERATE_STORY_CONTENT,
  async ({ generatedContentId }, { rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.post(`/api/story/re/generate?generatedContentId=${generatedContentId}`),
      {
        onError: (e) => {
          return rejectWithValue(e);
        },
      },
    );

    return { content: response.data };
  },
);

export const getMatchedStoryContent = createAsyncThunk(
  GET_MATCHED_STORY_CONTENT,
  async ({ generatedContentId }, { rejectWithValue }) => {
    const query = new URLSearchParams({ generatedContentId });
    const response = await sendRequest(() => callApi.get(`/api/story/get/matched?${query}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    return toLinkedContents(response.data);
  },
);

export const setStoryContent = createAsyncThunk(
  SET_STORY_CONTENT,
  async ({ generatedContentId, newId }, { rejectWithValue }) => {
    const query = new URLSearchParams({ generatedContentId, newId });
    const response = await sendRequest(() => callApi.post(`/api/story/set/content?${query}`), {
      onError: (e) => {
        return rejectWithValue(e);
      },
    });

    return { content: withCacheBustedLink(response.data, newId) };
  },
);

export const sendStory = createAsyncThunk(SEND_STORY, async ({ ids }, { dispatch, rejectWithValue }) => {
  await sendRequest(() => callApi.post('/api/story/send', ids), {
    onError: (e) => {
      if (e.response.status === 451) {
        dispatch(
          enqueueSnackbar({
            message: e.response.data,
            options: {
              variant: 'error',
            },
          }),
        );
      } else {
        dispatch(somethingWrongNotification());
      }

      return rejectWithValue(e);
    },
  });

  dispatch(
    enqueueSnackbar({
      message: 'contentSentSuccessfully',
      options: {
        variant: 'success',
      },
    }),
  );
});
